/**
 * useProjectionCalculations Hook
 *
 * Encapsulates all projection calculation logic for the ProjectionInputView component.
 * Provides memoized calculation functions for department totals, projection values,
 * unit counts, gross calculations, and expense projections.
 */

import { useCallback } from 'react';
import { DepartmentTemplate, ExpenseCategory, AccountDefinition } from '../config/docTemplate';
import { UnitCountsData } from '../services/api';
import { ProjectionInputRecord, ProjectionCalculatedRecord } from '../types/projections';
import {
  AccountData,
  DOCGrossData,
  INCOME_CATEGORIES,
  applySign
} from '../components/projections/projectionInputTypes';

/**
 * Department totals result from calculateDepartmentTotals
 */
export interface DepartmentTotals {
  sales: number;
  frontGross: number;
  fiIncome: number;
  totalGross: number;
  totalExpenses: number;
  operatingProfit: number;
  rentalIncome: number;
  units: number;
}

/**
 * Service department projections result
 */
export interface ServiceDeptProjections {
  gross: number;
  grossToSalesPercent: number;
  calculatedSales: number;
}

/**
 * Category account expenses result
 */
export interface CategoryAccountExpenses {
  totalPercent: number;
  totalAmount: number;
  accountExpenses: Record<string, { percent: number; amount: number }>;
}

/**
 * Calculated gross result
 */
export interface CalculatedGross {
  total: number;
  perUnit: number;
}

interface UseProjectionCalculationsProps {
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  inputs: ProjectionInputRecord[];
  // For consolidated view, use calculations instead of inputs
  calculations?: ProjectionCalculatedRecord[];
  // Consolidated view flag - changes how gross is calculated for vehicle departments
  isConsolidated?: boolean;
}

/**
 * Hook that provides all projection calculation functions
 */
export function useProjectionCalculations({ localInputs, inputs, calculations, isConsolidated = false }: UseProjectionCalculationsProps) {
  /**
   * Get account data with sign applied for a specific year
   */
  const getAccountWithSign = useCallback((
    def: AccountDefinition,
    categoryName: string,
    accountDataMap: Map<string, AccountData>
  ): number => {
    const data = accountDataMap.get(def.code);
    if (!data) return 0;

    const sign = def.sign || 'debit';
    const isIncomeCategory = INCOME_CATEGORIES.includes(categoryName);
    return applySign(data.ytdTotal, sign, isIncomeCategory);
  }, []);

  /**
   * Calculate category totals for a specific year
   */
  const calculateCategoryTotals = useCallback((
    category: ExpenseCategory,
    accountDataMap: Map<string, AccountData>
  ): number => {
    let ytd = 0;
    category.accounts.forEach(acc => {
      ytd += getAccountWithSign(acc, category.name, accountDataMap);
    });
    return ytd;
  }, [getAccountWithSign]);

  /**
   * Calculate department totals for a specific year
   */
  const calculateDepartmentTotals = useCallback((
    dept: DepartmentTemplate,
    docGrossMap: Map<string, DOCGrossData>,
    accountDataMap: Map<string, AccountData>,
    unitCountsData: UnitCountsData | null
  ): DepartmentTotals => {
    let salesYtd = 0;
    let frontGrossYtd = 0;
    let fiYtd = 0;
    let totalGrossYtd = 0;
    let rentalIncomeYtd = 0;
    let units = 0;

    // Map department name to DOC gross departments
    const deptToFrontGross: Record<string, string> = {
      'NEW_VEHICLE': 'NEW_VEHICLE',
      'USED_VEHICLE': 'USED_VEHICLE',
      'SERVICE': 'SERVICE',
      'PARTS': 'PARTS',
      'BODY_SHOP': 'BODY_SHOP',
      'OTHERS': 'OTHERS',
    };

    const deptToFI: Record<string, string> = {
      'NEW_VEHICLE': 'F_AND_I_NEW',
      'USED_VEHICLE': 'F_AND_I_USED',
    };

    // Get front gross and sales data
    const frontGrossDept = deptToFrontGross[dept.name];
    if (frontGrossDept) {
      const dg = docGrossMap.get(frontGrossDept);
      if (dg) {
        frontGrossYtd = dg.ytdGross;
        if (dg.ytdSales !== undefined) {
          salesYtd = dg.ytdSales;
        }
      }
    }

    // Get F&I
    const fiDept = deptToFI[dept.name];
    if (fiDept) {
      const fi = docGrossMap.get(fiDept);
      if (fi) {
        fiYtd = fi.ytdGross;
      }
    }

    // Total Gross = Front Gross + F&I
    totalGrossYtd = frontGrossYtd + fiYtd;

    // For Rental department
    if (dept.name === 'RENTAL') {
      const rentalIncomeCategory = dept.expenseCategories.find(c => c.name === 'RENTAL_INCOME');
      if (rentalIncomeCategory) {
        rentalIncomeYtd = calculateCategoryTotals(rentalIncomeCategory, accountDataMap);
      }
    }

    // Calculate total expenses
    let expenseYtd = 0;
    dept.expenseCategories.forEach(cat => {
      if (cat.name === 'RENTAL_INCOME') return;
      expenseYtd += calculateCategoryTotals(cat, accountDataMap);
    });

    // Calculate operating profit
    let profitYtd = 0;
    if (dept.name === 'RENTAL') {
      profitYtd = rentalIncomeYtd - expenseYtd;
    } else if (dept.name === 'MISC') {
      profitYtd = -expenseYtd;
    } else {
      profitYtd = totalGrossYtd - expenseYtd;
    }

    // Unit counts
    if (unitCountsData) {
      if (dept.name === 'NEW_VEHICLE') {
        units = unitCountsData.totalNewUnits || 0;
      } else if (dept.name === 'USED_VEHICLE') {
        units = unitCountsData.totalUsedUnits || 0;
      }
    }

    return {
      sales: salesYtd,
      frontGross: frontGrossYtd,
      fiIncome: fiYtd,
      totalGross: totalGrossYtd,
      totalExpenses: expenseYtd,
      operatingProfit: profitYtd,
      rentalIncome: rentalIncomeYtd,
      units,
    };
  }, [calculateCategoryTotals]);

  /**
   * Get projection value for a department/metric
   */
  const getProjectionValue = useCallback((department: string, metric: string): number | null => {
    const key = `${department}#${metric}`;

    // Check local inputs first
    if (localInputs[key] !== undefined) {
      return localInputs[key].value;
    }

    // Then check API inputs
    const apiInput = inputs.find(i => i.department === department && i.metric === metric);
    if (apiInput?.value !== undefined) {
      return apiInput.value;
    }

    // For consolidated view, check calculations (which contain annualTotal)
    if (calculations && calculations.length > 0) {
      const calc = calculations.find(c => c.department === department && c.metric === metric);
      if (calc?.annualTotal !== undefined) {
        return calc.annualTotal;
      }
    }

    return null;
  }, [localInputs, inputs, calculations]);

  /**
   * Get projected units per brand for a department
   */
  const getProjectedUnitsByBrand = useCallback((department: string): Record<string, number> => {
    const result: Record<string, number> = {};
    const prefix = `${department}#UNITS_`;

    // Check local inputs for brand-level units
    Object.keys(localInputs).forEach(key => {
      if (key.startsWith(prefix)) {
        const brand = key.replace(prefix, '');
        result[brand] = localInputs[key].value;
      }
    });

    // Also check API inputs for brand-level units
    inputs.forEach(input => {
      if (input.department === department && input.metric.startsWith('UNITS_')) {
        const brand = input.metric.replace('UNITS_', '');
        if (result[brand] === undefined) {
          result[brand] = input.value;
        }
      }
    });

    return result;
  }, [localInputs, inputs]);

  /**
   * Get total projected units (sum of all brand units)
   */
  const getTotalProjectedUnits = useCallback((department: string): number => {
    const brandUnits = getProjectedUnitsByBrand(department);
    const totalFromBrands = Object.values(brandUnits).reduce((sum, val) => sum + (val || 0), 0);

    // If there are NO brand-level unit inputs at all, fall back to the total UNITS
    // calculation (consolidated view). Brands entered as 0 mean 0 - falling back
    // there returned the old saved total and kept a revision to zero from saving.
    if (Object.keys(brandUnits).length === 0 && calculations && calculations.length > 0) {
      const calc = calculations.find(c => c.department === department && c.metric === 'UNITS');
      if (calc?.annualTotal !== undefined) {
        return calc.annualTotal;
      }
    }

    return totalFromBrands;
  }, [getProjectedUnitsByBrand, calculations]);

  /**
   * Calculate total gross from per-unit input
   *
   * For consolidated view (isConsolidated=true):
   * - Uses pre-calculated TOTAL_GROSS from CONSOLIDATED_SUMMARY
   * - Cannot split into Frontend/Backend (both GROSS and FI_INCOME return half of total)
   * - perUnit is calculated as average: totalGross / totalUnits
   *
   * For regular locations:
   * - Uses GROSS_PER_UNIT or FI_INCOME_PER_UNIT inputs multiplied by total units
   */
  const getCalculatedGross = useCallback((
    department: string,
    metric: 'GROSS' | 'FI_INCOME'
  ): CalculatedGross => {
    // For consolidated view, use pre-calculated values from calculations
    // These are the actual sums of dollar amounts across all locations
    if (isConsolidated && calculations && calculations.length > 0) {
      const totalUnits = getTotalProjectedUnits(department);

      // First, try to find the specific metric (GROSS or FI_INCOME)
      // These are the actual summed dollar values from all locations
      const specificCalc = calculations.find(
        c => c.department === department && c.metric === metric
      );

      if (specificCalc) {
        const total = specificCalc.annualTotal || 0;
        const avgPerUnit = totalUnits > 0 ? Math.round(total / totalUnits) : 0;
        return {
          total,
          perUnit: avgPerUnit,
        };
      }

      // Fallback: if we don't have separate GROSS/FI_INCOME, try TOTAL_GROSS with 50/50 split
      // This is for backwards compatibility before the consolidated loader was updated
      const totalGrossCalc = calculations.find(
        c => c.department === department && c.metric === 'TOTAL_GROSS'
      );

      if (totalGrossCalc) {
        const halfGross = Math.round(totalGrossCalc.annualTotal / 2);
        const avgPerUnit = totalUnits > 0 ? Math.round(halfGross / totalUnits) : 0;
        return {
          total: halfGross,
          perUnit: avgPerUnit,
        };
      }

      // No data found
      return { total: 0, perUnit: 0 };
    }

    // Regular location: use per-unit input × units
    const perUnit = getProjectionValue(department, `${metric}_PER_UNIT`) || 0;
    const totalUnits = getTotalProjectedUnits(department);
    return {
      total: perUnit * totalUnits,
      perUnit,
    };
  }, [getProjectionValue, getTotalProjectedUnits, isConsolidated, calculations]);

  /**
   * Get account-level expense projection (percentage)
   */
  const getAccountExpensePercent = useCallback((
    department: string,
    accountCode: string
  ): number | null => {
    const metric = `EXPENSE_${accountCode}`;
    return getProjectionValue(department, metric);
  }, [getProjectionValue]);

  /**
   * Get all account expense projections for a category
   */
  const getCategoryAccountExpenses = useCallback((
    department: string,
    accounts: AccountDefinition[],
    projectedGross: number
  ): CategoryAccountExpenses => {
    let totalPercent = 0;
    let totalAmount = 0;
    const accountExpenses: Record<string, { percent: number; amount: number }> = {};

    accounts.forEach(acc => {
      const percent = getAccountExpensePercent(department, acc.code);
      if (percent !== null) {
        const amount = Math.round((percent / 100) * projectedGross);
        totalPercent += percent;
        totalAmount += amount;
        accountExpenses[acc.code] = { percent, amount };
      }
    });

    return { totalPercent, totalAmount, accountExpenses };
  }, [getAccountExpensePercent]);

  /**
   * For Service/Parts/Body Shop/Rental: Get gross and gross-to-sales percentage
   * Sales = Gross / (GrossToSalesPercent / 100)
   */
  const getServiceDeptProjections = useCallback((department: string): ServiceDeptProjections => {
    const gross = getProjectionValue(department, 'GROSS') || 0;
    const grossToSalesPercent = getProjectionValue(department, 'GROSS_TO_SALES_PCT') || 0;
    const calculatedSales = grossToSalesPercent > 0 ? Math.round(gross / (grossToSalesPercent / 100)) : 0;
    return { gross, grossToSalesPercent, calculatedSales };
  }, [getProjectionValue]);

  return {
    // Pure calculation functions (no state dependencies)
    getAccountWithSign,
    calculateCategoryTotals,
    calculateDepartmentTotals,
    // Projection value functions (depend on localInputs/inputs)
    getProjectionValue,
    getProjectedUnitsByBrand,
    getTotalProjectedUnits,
    getCalculatedGross,
    getAccountExpensePercent,
    getCategoryAccountExpenses,
    getServiceDeptProjections,
  };
}
